import { Component, OnInit, OnDestroy } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { UserService } from 'src/app/_share/_services/user.service';
import { AuthorizationService } from 'src/app/_core/_services/authorization.service';
import { PortalManagementStatisticsService } from 'src/app/features/portal/_services/management/portal-management-statistics.service';
import { StatisticsService } from 'src/app/features/main-application/_services/statistics.service';
import { LeadStatsService } from 'src/app/features/crm/_services/lead-stats.service';
import { Router } from '@angular/router';
import { AREAS, AreaDefinition } from 'src/app/_core/_constants/area-permissions';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  portalStats: any = null;
  portalStatsLoading = true;

  deviceStats: any = null;
  deviceStatsLoading = true;

  orderStats: any = null;
  orderStatsLoading = true;

  leadStats: any = null;
  crmLoading = true;

  currentUserName = '';
  currentRoleDescription = '';

  /** Areas (excluding Home itself) the current user's PERMISSIONS unlock -
   * drives both the "Quick Links" buttons and which stats sections fetch
   * at all. Same AREAS source of truth as the route guards and sidebar, so
   * a button never links somewhere the guard will just reject. Gated on
   * AuthorizationService.hasPermission, never a role-name list. */
  accessibleAreas: AreaDefinition[] = [];

  canSeePortalStats = false;
  canSeeOperationsStats = false;
  canSeeServicesStats = false;
  canSeeCrmStats = false;

  private statsLoadedForPermissionSet = false;
  private subs: Subscription[] = [];

  constructor(
    private portalStatsService: PortalManagementStatisticsService,
    private statisticsService: StatisticsService,
    private leadStatsService: LeadStatsService,
    private userService: UserService,
    private authz: AuthorizationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subs.push(
      combineLatest([this.userService.currentUser$, this.authz.status$]).subscribe(
        ([user, status]) => {
          this.currentUserName = user.first_name || (user as any).username || user.email || '';
          this.currentRoleDescription =
            (user as any)?.roles?.[0]?.description ?? (user as any)?.role?.description ?? '';

          // Wait for a definitive permission result (loaded/error) AND real
          // user data (mirrors AuthGuard's hasData check) before gating
          // anything. On a hard refresh authz.status$ can reach 'loaded'
          // while currentUser$ still holds its initial {} default (the
          // superadmin bypass in AuthorizationService needs the real user
          // to recognise the role) - evaluating hasArea() against that
          // stale user computed canSeeXStats as false and permanently
          // tripped statsLoadedForPermissionSet, so the later emission with
          // the real user (canSeeXStats now true) never re-fetched stats -
          // the sections rendered but stayed empty until a full remount.
          const hasUserData = !!user && Object.keys(user as object).length > 0;
          if (!hasUserData || (status !== 'loaded' && status !== 'error')) {
            return;
          }

          this.accessibleAreas = Object.values(AREAS).filter(
            (area) => area.path !== AREAS.home.path && this.hasArea(area)
          );

          this.canSeePortalStats = this.hasArea(AREAS.portalManagement);
          this.canSeeOperationsStats = this.hasArea(AREAS.operations);
          this.canSeeServicesStats = this.hasArea(AREAS.services);
          this.canSeeCrmStats = this.hasArea(AREAS.crm);

          if (!this.statsLoadedForPermissionSet) {
            this.statsLoadedForPermissionSet = true;
            this.loadStatsForCurrentPermissions();
          }
        }
      )
    );
  }

  private hasArea(area: AreaDefinition): boolean {
    return !area.requiredPermission || this.authz.hasPermission(area.requiredPermission);
  }

  /** Only fetches the stats sections relevant to the user's permissions -
   * avoids both wasted/forbidden requests for areas they can't reach and a
   * page full of "no data available" placeholders that don't apply to
   * them. */
  private loadStatsForCurrentPermissions(): void {
    if (this.canSeePortalStats) {
      this.portalStatsService.getPortalOverviewStatistics().subscribe({
        next: (data) => {
          this.portalStats = data;
          this.portalStatsLoading = false;
        },
        error: () => {
          this.portalStatsLoading = false;
        }
      });
    } else {
      this.portalStatsLoading = false;
    }

    if (this.canSeeOperationsStats) {
      this.statisticsService.getDeviceStatistics().subscribe({
        next: (data) => {
          this.deviceStats = data;
          this.deviceStatsLoading = false;
        },
        error: () => {
          this.deviceStatsLoading = false;
        }
      });
    } else {
      this.deviceStatsLoading = false;
    }

    if (this.canSeeServicesStats) {
      this.statisticsService.getCurrentOrderTrends().subscribe({
        next: (data) => {
          this.orderStats = data;
          this.orderStatsLoading = false;
        },
        error: () => {
          this.orderStatsLoading = false;
        }
      });
    } else {
      this.orderStatsLoading = false;
    }

    if (this.canSeeCrmStats) {
      this.subs.push(
        this.leadStatsService.leadStats$.subscribe((data) => {
          this.leadStats = data;
          this.crmLoading = false;
        })
      );
      this.leadStatsService.getStatisticsLeads();
    } else {
      this.crmLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  // Mirrors StatisticsComponent.getTrendIconClass (pages/statistics) so the
  // Services tab's trend arrows match the rest of the app.
  getTrendIconClass(trend: string): string {
    switch (trend) {
      case 'up':
        return 'pi pi-arrow-up text-green-500';
      case 'down':
        return 'pi pi-arrow-down text-pink-500';
      case 'neutral':
        return 'pi pi-minus text-gray-500';
      default:
        return 'pi pi-question-circle text-gray-400';
    }
  }

  navigateTo(path: string[]): void {
    this.router.navigate(path);
  }
}
